import { t } from "i18next";
import { useState } from "react";
import type { NavigateFunction } from "react-router-dom";
import { Archive, CheckCircle2 } from "lucide-react";

import { FLASHPAY_ROLES, hasAnyRole } from "@/core/auth";
import { RowActionsMenu } from "@/core/components/shared/data-table";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useActivatePostingTemplate } from "@/features/ledger/api/use-activate-posting-template";
import { useArchivePostingTemplate } from "@/features/ledger/api/use-archive-posting-template";
import type { PostingTemplateRow } from "@/features/ledger/components/posting-templates/posting-templates-columns";

type PreviewKind = "activate" | "archive";

/**
 * Menu d'actions de ligne des modèles d'imputation.
 * Activation / archivage réservés à flashpay-superadmin / flashpay-admin
 * (masquage — le backend reste l'autorité finale). L'activation archive
 * automatiquement le modèle actif précédent du même couple
 * code produit / provider (guide §6.4).
 */
export function PostingTemplateRowActions({
  template,
  navigate: _navigate,
}: {
  template: PostingTemplateRow;
  navigate: NavigateFunction;
}) {
  const activate = useActivatePostingTemplate();
  const archive = useArchivePostingTemplate();
  const [preview, setPreview] = useState<PreviewKind | null>(null);

  const canManage = hasAnyRole([
    FLASHPAY_ROLES.SUPERADMIN,
    FLASHPAY_ROLES.ADMIN,
  ]);
  const pending = activate.isPending || archive.isPending;

  return (
    <>
      <RowActionsMenu
        actions={[
          ...(canManage && template.status !== "ACTIVE"
            ? [
                {
                  id: "activate",
                  label: t("ledger.templates.actions.activate"),
                  icon: CheckCircle2,
                  onClick: () => setPreview("activate"),
                },
              ]
            : []),
          ...(canManage && template.status !== "ARCHIVED"
            ? [
                {
                  id: "archive",
                  label: t("ledger.templates.actions.archive"),
                  icon: Archive,
                  variant: "destructive" as const,
                  onClick: () => setPreview("archive"),
                },
              ]
            : []),
        ]}
      />
      <MutationConfirmPreview
        open={preview !== null}
        onOpenChange={(open) => {
          if (!open) setPreview(null);
        }}
        data={{
          id: template.id,
          productCodeId: template.productCodeId,
          providerId: template.providerId,
          status: template.status,
          nextStatus: preview === "activate" ? "ACTIVE" : "ARCHIVED",
        }}
        title={
          preview === "archive"
            ? t("common.mutationPreview.deleteTitle")
            : t("common.mutationPreview.statusTitle")
        }
        description={t("common.mutationPreview.statusDescription")}
        confirmLabel={
          preview === "archive"
            ? t("ledger.templates.actions.archive")
            : t("ledger.templates.actions.activate")
        }
        t={t}
        pending={pending}
        onConfirm={() => {
          if (preview === "activate") {
            activate.mutate(template.id, {
              onSuccess: () => {
                notifSilence(t("ledger.templates.feedback.activated"));
                setPreview(null);
              },
              onError: (error) => notifSilenceError(error.message),
            });
            return;
          }
          archive.mutate(template.id, {
            onSuccess: () => {
              notifSilence(t("ledger.templates.feedback.archived"));
              setPreview(null);
            },
            onError: (error) => notifSilenceError(error.message),
          });
        }}
      />
    </>
  );
}
